import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth, useUser, useClerk } from '@clerk/clerk-react'

const API_BASE = process.env.REACT_APP_API_BASE_URL || ''

export default function AcceptInvite() {
  const { isSignedIn } = useUser()
  const { getToken } = useAuth()
  const { signOut } = useClerk()
  const nav = useNavigate()
  const loc = useLocation()

  const token = new URLSearchParams(loc.search).get('token')
  const [phase, setPhase] = useState('checking')
  const [msg, setMsg] = useState('')

  useEffect(() => {
    const run = async () => {
      if (!token) { setPhase('invalid'); setMsg('Missing invite token.'); return }

      try { localStorage.removeItem('org_jwt') } catch {}

      // One-time forced sign-out to guarantee a clean slate for this invite token.
      const signoutKey = `invite_signout_done:${token}`
      if (isSignedIn && !localStorage.getItem(signoutKey)) {
        localStorage.setItem(signoutKey, '1')
        setPhase('signing-out')
        await signOut({ redirectUrl: window.location.href })
        return
      }

      // Validate invite token (public)
      try {
        const v = await fetch(`${API_BASE}/invites/validate?token=${encodeURIComponent(token)}`)
        const j = await v.json()
        if (!j?.valid) { setPhase('invalid'); setMsg('This invite is invalid or expired.'); return }
      } catch { /* ignore network hiccups; proceed to UI */ }

      if (!isSignedIn) {
        setPhase('need-signup')
        return
      }

      // Accept (requires Clerk session)
      try {
        setPhase('accepting')
        const clerkToken = await getToken()
        if (!clerkToken) { setPhase('error'); setMsg('Failed to obtain session token.'); return }

        const res = await fetch(`${API_BASE}/invites/${encodeURIComponent(token)}/accept`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${clerkToken}` }
        })

        if (!res.ok) {
          let detail = 'Failed to accept invite.'
          try { const j = await res.json(); if (j?.error) detail = j.error } catch {}
          setPhase('error'); setMsg(detail); return
        }

        try { localStorage.removeItem('org_jwt') } catch {}
        setPhase('done')
        setTimeout(() => nav('/members'), 800)
      } catch (e) {
        setPhase('error'); setMsg(e?.message || 'Unexpected error while accepting invite.')
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, token])

  const redirectBack = `/invites/accept?token=${encodeURIComponent(token || '')}`

  return (
    <div className="mx-auto max-w-xl px-4 py-10">
      {phase === 'signing-out' && <p>Resetting your session…</p>}
      {phase === 'checking'   && <p>Checking your invite…</p>}
      {phase === 'accepting'  && <p>Accepting invite…</p>}
      {phase === 'done'       && <p>Invite accepted! Redirecting…</p>}
      {phase === 'invalid'    && <p>{msg || 'Sorry, this invite is invalid or expired.'}</p>}
      {phase === 'error'      && <p>{msg || 'Something went wrong.'}</p>}

      {phase === 'need-signup' && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Create your account</h2>
          <p>To join this organization, create your account below.</p>
          <a
            className="inline-block px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20"
            href={`/signup-choice?redirect_url=${encodeURIComponent(redirectBack)}`}
          >
            Create account
          </a>
        </div>
      )}
    </div>
  )
}
